import { useSiteSettings } from "@/context/SiteSettingsContext";
import { T } from "@/context/TextEditContext";

// Fallback clips used only when the admin has cleared both fields in
// Site Content → Homepage Banner Videos. Kept here so the homepage
// never renders a broken <video> tag if settings arrive empty.
const DEFAULT_OPEN_VIDEO_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_mobile-food-app-4/artifacts/m5j4cqih_gemini_generated_video_7da0a5da.mp4";

const DEFAULT_CLOSED_VIDEO_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_mobile-food-app-4/artifacts/kl58gnvj_gemini_generated_video_c13b16ae.mp4";

/**
 * StatusBanner — one banner that switches between two looping videos based
 * on the admin's `orders_open` master switch:
 *   OPEN   → cheerful "we're open, order now!" clip with a green "Open Now" pulse
 *   CLOSED → paused/closed clip with the admin-editable message overlay
 *
 * The two clip URLs are admin-editable in Site Content → Homepage Banner
 * Videos (fields `open_banner_video_url` / `closed_banner_video_url`).
 * Videos are muted / playsinline / autoplay / loop so they run without a
 * user gesture on iOS + Android and never grab audio focus.
 */
export default function StatusBanner() {
  const { settings } = useSiteSettings();
  const isOpen = settings?.orders_open !== false;

  const openUrl =
    (settings?.open_banner_video_url || "").trim() || DEFAULT_OPEN_VIDEO_URL;
  const closedUrl =
    (settings?.closed_banner_video_url || "").trim() ||
    DEFAULT_CLOSED_VIDEO_URL;

  if (isOpen) {
    return (
      <div
        data-testid="open-now-banner"
        className="mx-4 mt-3 rounded-2xl overflow-hidden border-4 border-brand-red shadow-lg relative"
      >
        <video
          key={openUrl}
          src={openUrl}
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          onContextMenu={(e) => e.preventDefault()}
          aria-label="We're open — order now"
          className="w-full h-auto block bg-stone-900 select-none pointer-events-none"
          style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
        />
        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-emerald-500 text-white px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-black shadow-lg">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          <T k="banner.open_pill" d="Open Now" />
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="orders-closed-banner"
      className="mx-4 mt-3 rounded-2xl overflow-hidden border-4 border-brand-red shadow-lg relative"
    >
      <video
        key={closedUrl}
        src={closedUrl}
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        onContextMenu={(e) => e.preventDefault()}
        aria-label="Online ordering is paused"
        className="w-full h-auto block bg-stone-900 select-none pointer-events-none"
        style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
      />
      <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-brand-red text-white px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-black shadow-lg">
        <span className="w-2 h-2 rounded-full bg-white" />
        Ordering Paused
      </div>
      <div className="bg-brand-red text-white px-4 py-3 text-center">
        <div className="font-hand text-brand-yellow text-3xl leading-tight">
          <T k="banner.paused_headline" d="Online ordering is paused" />
        </div>
        <div className="text-xs text-white/90 mt-1 leading-snug">
          <T
            field="orders_closed_message"
            d="You're a little early — please try during our hours of operation."
          />
        </div>
      </div>
    </div>
  );
}
